import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Package, CreditCard, Archive } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TransportRecap() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [data, setData] = useState({ unpaid: [], paid: [], eur_vnd_rate: 30000 });
  const [selected, setSelected] = useState({});
  const isAdmin = user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/transport-recap`, { withCredentials: true });
      setData(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSelect = (id) => {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const selectAll = () => {
    const allIds = {};
    data.unpaid.forEach(o => { allIds[o._id] = true; });
    setSelected(allIds);
  };

  const selectedOrders = data.unpaid.filter(o => selected[o._id]);
  const totalVnd = selectedOrders.reduce((sum, o) => sum + (o.package_price_vnd || 0), 0);
  const totalEur = data.eur_vnd_rate > 0 ? Math.round(totalVnd / data.eur_vnd_rate * 100) / 100 : 0;

  const handlePay = async () => {
    const ids = selectedOrders.map(o => o._id);
    if (!ids.length) return;
    if (!window.confirm(`Confirmer le paiement de ${ids.length} commande(s) pour ${totalVnd.toLocaleString()} VND (${totalEur.toFixed(2)} €) ?`)) return;
    try {
      await axios.post(`${API}/api/transport-recap/pay`, { order_ids: ids }, { withCredentials: true });
      toast.success(`${ids.length} commande(s) payée(s)`);
      setSelected({});
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold text-slate-900">{t('transport_recap_title')}</h1>

        {/* Unpaid section */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Package size={18} /> {t('transport_unpaid')} ({data.unpaid.length})</CardTitle>
              {isAdmin && data.unpaid.length > 0 && (
                <Button size="sm" variant="outline" onClick={selectAll} className="text-xs">{t('select_all')}</Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {data.unpaid.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">{t('no_transport_orders')}</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-[32px_1fr_100px_80px_80px_80px_80px] gap-2 text-xs font-semibold text-slate-500 uppercase px-2">
                  {isAdmin && <span></span>}
                  {!isAdmin && <span></span>}
                  <span>{t('order_name')}</span>
                  <span>{t('order_date_col')}</span>
                  <span>{t('status')}</span>
                  <span>{t('weight_kg')}</span>
                  <span>VND</span>
                  <span>EUR</span>
                </div>
                {data.unpaid.map(o => (
                  <div key={o._id} className="grid grid-cols-[32px_1fr_100px_80px_80px_80px_80px] gap-2 items-center px-2 py-2 rounded hover:bg-slate-50 border border-slate-100">
                    {isAdmin ? (
                      <Checkbox checked={!!selected[o._id]} onCheckedChange={() => toggleSelect(o._id)} data-testid={`select-${o._id}`} />
                    ) : <span></span>}
                    <span className="text-sm font-medium truncate">{o.client_name || o.order_number}</span>
                    <span className="text-xs text-slate-500">{o.order_date ? new Date(o.order_date).toLocaleDateString('fr-FR') : '—'}</span>
                    <Badge className="bg-blue-100 text-blue-800 text-xs truncate">{o.status}</Badge>
                    <span className="text-sm font-medium">{o.package_weight ? `${o.package_weight} kg` : '—'}</span>
                    <span className="text-sm font-bold">{(o.package_price_vnd || 0).toLocaleString()}</span>
                    <span className="text-sm font-bold text-emerald-700">{(o.package_price_eur || 0).toFixed(2)} €</span>
                  </div>
                ))}
              </div>
            )}

            {/* Payment bar for admin */}
            {isAdmin && selectedOrders.length > 0 && (
              <div className="mt-4 flex items-center justify-between bg-slate-900 text-white rounded-lg px-4 py-3" data-testid="payment-bar">
                <div className="flex items-center gap-4">
                  <span className="text-sm">{selectedOrders.length} commande(s)</span>
                  <span className="font-bold">{totalVnd.toLocaleString()} VND</span>
                  <span className="font-bold text-emerald-400">{totalEur.toFixed(2)} €</span>
                  <span className="text-xs text-slate-400">(1 EUR = {data.eur_vnd_rate.toLocaleString()} VND)</span>
                </div>
                <Button className="bg-emerald-600 hover:bg-emerald-700 gap-1" onClick={handlePay} data-testid="pay-btn">
                  <CreditCard size={16} /> {t('mark_as_paid')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Paid archives */}
        {data.paid.length > 0 && (
          <div className="space-y-3" data-testid="paid-archives">
            <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
              <Archive size={18} className="text-slate-500" />
              <h2 className="text-lg font-semibold text-slate-700">{t('transport_paid_archives')}</h2>
            </div>
            <Accordion type="multiple" className="space-y-2">
              {data.paid.map((group, gi) => {
                const groupTotal = group.orders.reduce((s, o) => s + (o.package_price_vnd || 0), 0);
                const groupEur = data.eur_vnd_rate > 0 ? Math.round(groupTotal / data.eur_vnd_rate * 100) / 100 : 0;
                return (
                  <AccordionItem key={gi} value={`g-${gi}`} className="border border-slate-200 rounded-lg overflow-hidden">
                    <AccordionTrigger className="hover:no-underline px-4 py-3 bg-emerald-50 hover:bg-emerald-100">
                      <div className="flex items-center justify-between w-full pr-2">
                        <span className="text-sm font-semibold text-slate-700">
                          {t('payment_date')} : {group.payment_date ? new Date(group.payment_date).toLocaleDateString('fr-FR') : '—'}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-500">{group.orders.length} commande(s)</span>
                          <Badge className="bg-emerald-200 text-emerald-800">{groupTotal.toLocaleString()} VND / {groupEur.toFixed(2)} €</Badge>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="divide-y divide-slate-100">
                        {group.orders.map(o => (
                          <div key={o._id} className="flex items-center justify-between py-2 px-4 text-sm">
                            <span className="font-medium">{o.client_name || o.order_number}</span>
                            <span className="text-xs text-slate-500">{o.order_date ? new Date(o.order_date).toLocaleDateString('fr-FR') : ''}</span>
                            <span>{o.package_weight ? `${o.package_weight} kg` : '—'}</span>
                            <span className="font-bold">{(o.package_price_vnd || 0).toLocaleString()} VND</span>
                            <span className="font-bold text-emerald-700">{(o.package_price_eur || 0).toFixed(2)} €</span>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}
      </div>
    </Layout>
  );
}
